import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DealItem,
  DealItemWithVersions,
  ValidationError,
} from '@/lib/graphql/generated/graphql';
import { useTranslateValidationError } from '@/lib/hooks/useTranslateValidationError';
import { cn } from '@/lib/utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { effectiveValidationErrorsAtom } from '@/states/effective-validation-errors.state';
import { ExtendedValidationError } from '@/types/validation-error';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { AlertCircle, AlertTriangle } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';

interface ValidationErrorsCellProps {
  rawValidationErrors: (ValidationError & {
    variantName?: string;
  })[];
  isParent: boolean;
}

export function getErrorIcon(severity: ExtendedValidationError['severity']) {
  switch (severity) {
    case 'error':
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    default:
      return null;
  }
}

const ValidationErrorsCell: React.FC<ValidationErrorsCellProps> = ({
  rawValidationErrors,
  isParent,
}) => {
  const { t } = useTranslation();
  const translateValidationError = useTranslateValidationError();

  // Memoize translated errors - only recompute when raw errors or translation function change
  const translatedErrors = useMemo(() => {
    const severityRank: Record<string, number> = { error: 0, warning: 1 };
    return rawValidationErrors
      .map((error) => {
        const translated = translateValidationError(error);
        return {
          ...translated,
          variantName: error.variantName,
        };
      })
      .sort(
        (a, b) =>
          (severityRank[a.severity] ?? 2) - (severityRank[b.severity] ?? 2)
      );
  }, [rawValidationErrors, translateValidationError]);

  const getMainErrorIcon = (validationErrors: ExtendedValidationError[]) => {
    const hasError = validationErrors.some(
      (error) => error.severity === 'error'
    );
    if (hasError) {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
    return <AlertTriangle className="h-4 w-4 text-orange-400" />;
  };

  return (
    <div className="flex w-full flex-col items-center space-y-2 p-4 pl-3 text-left">
      {translatedErrors.length > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn('cursor-help')}>
              {getMainErrorIcon(translatedErrors)}
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="left"
            className="w-80 rounded-lg border border-gray-200 bg-white p-0 shadow-lg"
          >
            <div className="border-b border-gray-100 p-3">
              <h3 className="font-medium text-gray-900">
                {t('general.errors_to_fix')}
              </h3>
            </div>
            <div className="p-0">
              {translatedErrors.map((error, idx) => (
                <div
                  key={idx}
                  className="border-b border-gray-100 p-2 last:border-0"
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5">{getErrorIcon(error.severity)}</div>
                    <div>
                      {isParent && error.variantName && (
                        <div className="mb-1 text-xs font-medium text-gray-500">
                          {t('general.variant')} : {error.variantName}
                        </div>
                      )}
                      <div className="text-sm font-medium">
                        {error.ruleName}
                      </div>
                      <div className="mt-0.5 text-sm text-gray-600">
                        {error.message}
                      </div>
                      {error.fixSuggestion && (
                        <div className="mt-1 text-xs italic text-blue-600">
                          {t('general.error_suggestion')}
                          {' : '}
                          {error.fixSuggestion}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      ) : (
        <div className="text-gray-300">—</div>
      )}
    </div>
  );
};

const ValidationErrorsColumnCell: React.FC<{
  row: Row<DealItemWithVersions>;
}> = ({ row }) => {
  const effectiveErrors = useAtomValue(effectiveValidationErrorsAtom);
  const isParent = row.subRows.length > 1;

  let rawValidationErrors: (ValidationError & { variantName?: string })[] = [];

  if (isParent) {
    if (row.subRows.length === 1) {
      rawValidationErrors = [];
    } else {
      const parentErrorsMap = new Map<
        ValidationError['ruleId'],
        ValidationError
      >();
      row.subRows.forEach((subRow) => {
        const errors = effectiveErrors.get(subRow.original.id) ?? [];
        errors.forEach((error) => {
          if (!parentErrorsMap.has(error.ruleId)) {
            parentErrorsMap.set(error.ruleId, error);
          }
        });
      });
      rawValidationErrors = Array.from(parentErrorsMap.values());
    }
  } else {
    rawValidationErrors = effectiveErrors.get(row.original.id) ?? [];
  }

  return (
    <ValidationErrorsCell
      rawValidationErrors={rawValidationErrors}
      isParent={isParent}
    />
  );
};

const useColumnDefValidationErrors =
  (): ExtendedColumnDef<DealItemWithVersions> => {
    const { setColumnSort } = useSetColumnSort();

    const accessorKey: keyof DealItem = 'validationErrors';

    return useMemo(
      () => ({
        accessorKey: accessorKey,
        name: 'ValidationErrors',
        accessorFn: (row) => {
          const errors = row.activeVersion?.validationErrors;
          return errors ? errors.length : 0;
        },
        sortingFn: 'alphanumeric',
        header: () => (
          <div className="flex items-center gap-1">
            <AlertTriangle className="h-4 w-4 text-orange-400" />
            <div
              className="flex flex-1 items-center gap-2"
              onClick={() => setColumnSort(accessorKey)}
            >
              <SortIcon pointer="validationErrors" />
            </div>
          </div>
        ),
        cell: ({ row }) => <ValidationErrorsColumnCell row={row} />,
        size: 60,
      }),
      [setColumnSort]
    );
  };

export default useColumnDefValidationErrors;
